// frontend/src/pages/SchoolProjects/TechnicianParts/AssignmentsList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Pagination,
  Divider,
  Badge
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  CalendarToday as CalendarIcon,
  KeyboardReturn as ReturnIcon,
  Done as DoneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

const AssignmentsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    technician_id: '',
    school_id: '',
    part_id: '',
    status: '',
    start_date: '',
    end_date: '',
    search_text: ''
  });
  
  // Data for filters
  const [technicians, setTechnicians] = useState([]);
  const [schools, setSchools] = useState([]);
  const [parts, setParts] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, assignments]);

  const loadData = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      // Load all data in parallel
      const [assignmentsRes, techsRes, schoolsRes, partsRes] = await Promise.all([
        api.getTechnicianAssignments(),
        api.getTechniciansList(),
        api.getSchools(),
        api.getComputerParts()
      ]);
      
      setAssignments(assignmentsRes.data || []);
      setFilteredAssignments(assignmentsRes.data || []);
      setTotalCount(assignmentsRes.data?.length || 0);
      
      setTechnicians(techsRes.data || []);
      setSchools(schoolsRes.data || []);
      setParts(partsRes.data || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Failed to load assignments. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate returned quantity from assignment data
  const getReturnedQuantity = (assignment) => {
    // Check if assignment has return information in the data
    if (assignment.returned_quantity !== undefined) {
      return parseInt(assignment.returned_quantity) || 0;
    }
    
    // Check if assignment has partial returns information
    if (assignment.partial_returns && Array.isArray(assignment.partial_returns)) {
      return assignment.partial_returns.reduce((total, returnItem) => 
        total + (parseInt(returnItem.quantity) || 0), 0);
    }
    
    // If status is 'returned', assume all items were returned
    if (assignment.status === 'returned') {
      return parseInt(assignment.quantity) || 0;
    }
    
    // If status is 'partially_returned', check for partial return data
    if (assignment.status === 'partially_returned' && assignment.actual_return_date) {
      // For partially returned, you might need to check the actual data structure
      // This is a fallback - you should adjust based on your actual data structure
      return Math.floor(parseInt(assignment.quantity) / 2) || 0; // Just an example
    }
    
    return 0;
  };

  const applyFilters = () => {
    let filtered = [...assignments];
    
    // Apply technician filter
    if (filters.technician_id) {
      filtered = filtered.filter(a => a.technician_id == filters.technician_id);
    }
    
    // Apply school filter
    if (filters.school_id) {
      filtered = filtered.filter(a => a.school_id == filters.school_id);
    }
    
    // Apply part filter
    if (filters.part_id) {
      filtered = filtered.filter(a => a.part_id == filters.part_id);
    }
    
    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(a => a.status === filters.status);
    }
    
    // Apply date range filter
    if (filters.start_date) {
      const start = new Date(filters.start_date);
      filtered = filtered.filter(a => new Date(a.assignment_date) >= start);
    }
    
    if (filters.end_date) {
      const end = new Date(filters.end_date);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(a => new Date(a.assignment_date) <= end);
    }
    
    // Apply text search
    if (filters.search_text) {
      const searchTerm = filters.search_text.toLowerCase();
      filtered = filtered.filter(a => 
        (a.ComputerPart?.part_name?.toLowerCase().includes(searchTerm)) ||
        (a.ComputerPart?.part_number?.toLowerCase().includes(searchTerm)) ||
        (a.Technician?.name?.toLowerCase().includes(searchTerm)) ||
        (a.School?.school_name?.toLowerCase().includes(searchTerm)) ||
        (a.notes?.toLowerCase().includes(searchTerm))
      );
    }
    
    setFilteredAssignments(filtered);
    setTotalCount(filtered.length);
    setPage(1); // Reset to first page when filters change
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      technician_id: '',
      school_id: '',
      part_id: '',
      status: '',
      start_date: '',
      end_date: '',
      search_text: ''
    });
  };

  const handleViewDetails = (assignment) => {
    setSelectedAssignment(assignment);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedAssignment(null);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'issued': return 'primary';
      case 'overdue': return 'error';
      case 'partially_returned': return 'warning';
      case 'returned': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'issued': return <AssignmentIcon color="primary" />;
      case 'overdue': return <AssignmentIcon color="error" />;
      case 'partially_returned': return <AssignmentIcon color="warning" />;
      case 'returned': return <AssignmentIcon color="success" />;
      default: return null;
    }
  };

  // UPDATED: Export to CSV function with return quantity
  const exportToCSV = () => {
    // Updated headers with return information
    const headers = [
      'Assignment ID', 
      'Technician Name', 
      'Technician Email',
      'Technician Phone',
      'Part Name', 
      'Part Number', 
      'Category',
      'Manufacturer',
      'School Name', 
      'School Address',
      'Contact Person',
      'Project Name',
      'Issued Quantity', 
      'Returned Quantity',
      'Pending Quantity',
      'Issued Date', 
      'Expected Return Date', 
      'Actual Return Date',
      'Status',
      'Return Status',
      'Assignment Notes'
    ];
    
    // Create CSV data with return information
    const csvData = filteredAssignments.map(assignment => {
      const returnedQty = getReturnedQuantity(assignment);
      const issuedQty = parseInt(assignment.quantity) || 0;
      const pendingQty = Math.max(0, issuedQty - returnedQty);
      
      // Determine return status based on returned quantity
      let returnStatus = 'Not Returned';
      if (returnedQty > 0) {
        if (returnedQty === issuedQty) {
          returnStatus = 'Fully Returned';
        } else {
          returnStatus = 'Partially Returned';
        }
      }
      
      return [
        assignment.id,
        assignment.Technician?.name || 'N/A',
        assignment.Technician?.email || 'N/A',
        assignment.Technician?.phone || 'N/A',
        assignment.ComputerPart?.part_name || 'N/A',
        assignment.ComputerPart?.part_number || 'N/A',
        assignment.ComputerPart?.category || 'N/A',
        assignment.ComputerPart?.manufacturer || 'N/A',
        assignment.School?.school_name || 'N/A',
        assignment.School?.school_address || 'N/A',
        assignment.School?.contact_person || 'N/A',
        assignment.SchoolProject?.project_name || 'N/A',
        issuedQty,
        returnedQty,
        pendingQty,
        new Date(assignment.assignment_date).toLocaleDateString(),
        assignment.expected_return_date ? 
          new Date(assignment.expected_return_date).toLocaleDateString() : 'Not set',
        assignment.actual_return_date ? 
          new Date(assignment.actual_return_date).toLocaleDateString() : 'Not returned',
        assignment.status,
        returnStatus,
        assignment.notes || ''
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `technician_assignments_with_returns_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    setExportDialogOpen(false);
    setMessage({ type: 'success', text: 'Export completed successfully with return information!' });
  };

  // Calculate pagination
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedAssignments = filteredAssignments.slice(startIndex, endIndex);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getStats = () => {
    const stats = {
      total: assignments.length,
      issued: assignments.filter(a => a.status === 'issued').length,
      overdue: assignments.filter(a => a.status === 'overdue').length,
      returned: assignments.filter(a => a.status === 'returned').length,
      partially_returned: assignments.filter(a => a.status === 'partially_returned').length
    };
    return stats;
  };

  const stats = getStats();

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/school-projects/technician-parts')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" gutterBottom>
            Technician Assignments
          </Typography>
          <Typography variant="body1" color="textSecondary">
            View and manage all parts assignments to technicians
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate('/school-projects/technician-parts/issue')}
          >
            New Assignment
          </Button>
        </Box>
      </Box>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {stats.issued}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error">
                {stats.overdue}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Overdue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success">
                {stats.returned}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Returned
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning">
                {stats.partially_returned}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Partial
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4">
                {filteredAssignments.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Filtered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={filters.search_text}
              onChange={(e) => setFilters(prev => ({ ...prev, search_text: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Technician</InputLabel>
              <Select
                name="technician_id"
                value={filters.technician_id}
                onChange={handleFilterChange}
                label="Technician"
              >
                <MenuItem value="">All</MenuItem>
                {technicians.map(tech => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>School</InputLabel>
              <Select
                name="school_id"
                value={filters.school_id}
                onChange={handleFilterChange}
                label="School"
              >
                <MenuItem value="">All</MenuItem>
                {schools.map(school => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.school_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="issued">Issued</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="partially_returned">Partially Returned</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={filters.start_date}
              onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={filters.end_date}
              onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} display="flex" justifyContent="space-between" mt={1}>
            <Button
              variant="outlined"
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
            <Typography variant="body2" color="textSecondary">
              Showing {filteredAssignments.length} of {assignments.length} assignments
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Assignments Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Technician</TableCell>
                    <TableCell>Part Details</TableCell>
                    <TableCell>School</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Issued Date</TableCell>
                    <TableCell>Expected Return</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAssignments.map((assignment) => {
                    const returnedQty = getReturnedQuantity(assignment);
                    const issuedQty = parseInt(assignment.quantity) || 0;
                    
                    return (
                      <TableRow 
                        key={assignment.id}
                        hover
                        sx={{ 
                          backgroundColor: assignment.status === 'overdue' ? 'error.light' : 'inherit',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            #{assignment.id}
                          </Typography>
                          {returnedQty > 0 && (
                            <Badge 
                              badgeContent={returnedQty} 
                              color="success" 
                              size="small"
                              sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: '16px', minWidth: '16px' } }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2">
                              {assignment.Technician?.name || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {assignment.ComputerPart?.part_name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {assignment.ComputerPart?.part_number || 'N/A'} | {assignment.ComputerPart?.category || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <SchoolIcon sx={{ mr: 1, fontSize: 16, color: 'secondary.main' }} />
                            <Typography variant="body2">
                              {assignment.School?.school_name || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {assignment.SchoolProject?.project_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {assignment.quantity}
                            </Typography>
                            {returnedQty > 0 && (
                              <Typography variant="caption" color="success.main">
                                {returnedQty} returned
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <CalendarIcon sx={{ mr: 1, fontSize: 14 }} />
                            <Typography variant="body2">
                              {new Date(assignment.assignment_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {assignment.expected_return_date ? (
                            <Chip 
                              label={new Date(assignment.expected_return_date).toLocaleDateString()}
                              size="small"
                              color={new Date(assignment.expected_return_date) < new Date() ? "error" : "default"}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              Not set
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(assignment.status)}
                            label={assignment.status.replace('_', ' ').toUpperCase()}
                            size="small"
                            color={getStatusColor(assignment.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small"
                              onClick={() => handleViewDetails(assignment)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(assignment.status === 'issued' || assignment.status === 'overdue') && (
                            <Tooltip title="Return">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => navigate(`/school-projects/technician-parts/return`)}
                                sx={{ ml: 1 }}
                              >
                                Return
                              </Button>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            {filteredAssignments.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  No assignments found matching your filters
                </Typography>
              </Box>
            )}
            
            {/* Pagination */}
            {filteredAssignments.length > 0 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={Math.ceil(filteredAssignments.length / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Assignment Details Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        {selectedAssignment && (
          <>
            <DialogTitle>
              Assignment Details
              <Typography variant="caption" display="block" color="textSecondary">
                ID: #{selectedAssignment.id}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Assignment Information
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Status:</strong> 
                        <Chip 
                          label={selectedAssignment.status.replace('_', ' ').toUpperCase()}
                          size="small"
                          color={getStatusColor(selectedAssignment.status)}
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Issued Date:</strong> {new Date(selectedAssignment.assignment_date).toLocaleDateString()}
                      </Typography>
                      {selectedAssignment.expected_return_date && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Expected Return:</strong> {new Date(selectedAssignment.expected_return_date).toLocaleDateString()}
                        </Typography>
                      )}
                      {selectedAssignment.actual_return_date && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Actual Return:</strong> {new Date(selectedAssignment.actual_return_date).toLocaleDateString()}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Quantity:</strong> {selectedAssignment.quantity}
                      </Typography>
                      {/* ADDED: Return quantity display */}
                      {(() => {
                        const returnedQty = getReturnedQuantity(selectedAssignment);
                        if (returnedQty > 0) {
                          return (
                            <Typography variant="body2" color="success.main">
                              <strong>Returned:</strong> {returnedQty}
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Part Information
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Part Name:</strong> {selectedAssignment.ComputerPart?.part_name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Part Number:</strong> {selectedAssignment.ComputerPart?.part_number || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Category:</strong> {selectedAssignment.ComputerPart?.category || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Manufacturer:</strong> {selectedAssignment.ComputerPart?.manufacturer || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Specifications:</strong> {selectedAssignment.ComputerPart?.specifications || 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        People & Locations
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Technician:</strong> {selectedAssignment.Technician?.name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Phone:</strong> {selectedAssignment.Technician?.phone || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Email:</strong> {selectedAssignment.Technician?.email || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Specialization:</strong> {selectedAssignment.Technician?.specialization || 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        School & Project
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>School:</strong> {selectedAssignment.School?.school_name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Address:</strong> {selectedAssignment.School?.school_address || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Contact Person:</strong> {selectedAssignment.School?.contact_person || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Project:</strong> {selectedAssignment.SchoolProject?.project_name || 'Not assigned to project'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {selectedAssignment.notes && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Notes
                        </Typography>
                        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                          {selectedAssignment.notes}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>Close</Button>
              {(selectedAssignment.status === 'issued' || selectedAssignment.status === 'overdue') && (
                <Button 
                  variant="contained"
                  onClick={() => {
                    closeDialog();
                    navigate('/school-projects/technician-parts/return');
                  }}
                >
                  Process Return
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Assignments</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Export {filteredAssignments.length} assignments to CSV format with return information.
          </Typography>
          <Typography variant="caption" color="textSecondary">
            The export will include:
            <ul>
              <li>Assignment details</li>
              <li>Issued quantity</li>
              <li>Returned quantity</li>
              <li>Pending quantity</li>
              <li>Return status</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={exportToCSV} variant="contained" startIcon={<DownloadIcon />}>
            Export CSV with Returns
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message.text}
        autoHideDuration={6000}
        onClose={() => setMessage({ type: '', text: '' })}
      >
        <Alert 
          severity={message.type}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssignmentsList;